import { toValue, type MaybeRefOrGetter } from 'vue'
import type { Product } from '@scayle/storefront-nuxt'
import { useNuxtApp } from '#app/nuxt'
import { onNuxtReady } from '#app/composables/ready'
// `~` resolves to the consuming app at build time, so this picks up the
// app-shadowed tracking composable (same pattern as plp/usePlpTracking.ts).
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import type { CarouselListContext } from '../constants/recommendations'

// BasicSlider's `slideVisible` emits items already carrying a 0-based global index.
type ListedProductLike = Product & { index: number }

/**
 * Shared impression + click tracking for recommendation carousels.
 *
 * Wires a carousel's `BasicSlider @slide-visible` and `@click:recommendation`
 * into the existing GA4 `view_item_list` / `select_item` builders, adding:
 *  - per-instance impression dedup keyed on product `id` (BasicSlider spreads
 *    fresh objects each emit, so identity-based dedup cannot work — see ECOM-2618),
 *  - empty-list skip,
 *  - SSR hydration deferral (Intersect's `@enter.once` fires only once, so an
 *    impression that lands mid-hydration is deferred past hydration, never dropped).
 *
 * The raw 0-based index is forwarded to the builders unchanged — they add +1
 * internally, so converting here would double-count.
 */
export default function useCarouselListTracking(options: {
  context: MaybeRefOrGetter<CarouselListContext>
  total?: MaybeRefOrGetter<number | undefined>
  // Optional impression gate, evaluated at emit time. Lets a carousel suppress
  // impressions for its own reason (e.g. AGS passes `() => !goControl.value` to
  // skip Odoscope control-group lists). Omit to always track.
  shouldTrack?: MaybeRefOrGetter<boolean>
}) {
  const { trackViewItemList, trackSelectItem } = useTrackingEvents()
  const nuxtApp = useNuxtApp()
  const seenIds = new Set<number>()
  // Holds the most recent visibility payload seen while hydrating, flushed once
  // `onNuxtReady` fires. Always the latest — never a frozen first-emit snapshot.
  let pendingImpression: {
    items: ListedProductLike[]
    slideCount: number
    slideIndex: number
  } | null = null
  let flushScheduled = false

  const fireImpression = (
    items: ListedProductLike[],
    slideCount: number,
    slideIndex: number,
  ) => {
    if (!items?.length) {
      return
    }

    if (options.shouldTrack !== undefined && !toValue(options.shouldTrack)) {
      return
    }

    const untracked = items.filter((product) => !seenIds.has(product.id))
    if (!untracked.length) {
      return
    }

    const { itemListType, itemListName } = toValue(options.context)

    const pushed = trackViewItemList({
      type: itemListType,
      name: itemListName,
      items: untracked,
      filters: '',
      filterOptions: '',
      sorting: '',
      totalItemQuantity: toValue(options.total) ?? items.length,
      totalPages: slideCount || 1,
      viewItemQuantity: untracked.length,
      currentPage: (slideIndex || 0) + 1,
    })

    // Mark products seen only once the impression is confirmed sent. `track`
    // no-ops (without throwing) when `$tracking` isn't ready yet; marking before
    // the push would permanently drop the impression because BasicSlider's
    // Intersect fires `@enter.once` and never re-emits for this carousel.
    if (pushed) {
      untracked.forEach((product) => seenIds.add(product.id))
    }
  }

  const onSlideVisible = (
    items: ListedProductLike[],
    slideCount = 1,
    slideIndex = 0,
  ) => {
    // Impressions are a client-only concern (the DataLayer sink no-ops on server).
    if (import.meta.server) {
      return
    }

    if (nuxtApp.isHydrating) {
      // Defer rather than drop: Intersect emits `@enter.once`, so suppressing the
      // impression during hydration would lose it for good. Record the *latest*
      // payload — a mid-hydration emit can fire before Swiper has settled its
      // active slide, so a later emit may carry the correct visible items/index;
      // overwriting (rather than freezing the first emit and ignoring the rest)
      // keeps the most accurate one. Flush once via `onNuxtReady`, which runs the
      // callback whether or not hydration is still in flight, so the impression
      // survives even when it lands after `app:suspense:resolve` has passed.
      pendingImpression = { items, slideCount, slideIndex }
      if (!flushScheduled) {
        flushScheduled = true
        onNuxtReady(() => {
          if (pendingImpression) {
            const pending = pendingImpression
            pendingImpression = null
            fireImpression(
              pending.items,
              pending.slideCount,
              pending.slideIndex,
            )
          }
        })
      }
      return
    }

    fireImpression(items, slideCount, slideIndex)
  }

  const onSelect = (product: Product, index: number) => {
    // Gate selects the same way impressions are gated, so control-group users
    // emit neither view_item_list nor select_item (the latter also drives PDP
    // attribution via productTracking localStorage in trackSelectItem).
    if (options.shouldTrack !== undefined && !toValue(options.shouldTrack)) {
      return
    }

    const { itemListType, itemListName } = toValue(options.context)
    trackSelectItem({
      product,
      index,
      listType: itemListType,
      listName: itemListName,
    })
  }

  return { onSlideVisible, onSelect }
}
